import { useConnectorResources } from "@/features/Data/hooks/useConnectorResources";
import { renamePromptbookStep } from "@/features/PromptbookEditor/actions/renamePromptbookStep";
import { useConnection } from "@/features/PromptbookRuntime/connection/useConnection";
import { useStateUpdate } from "@/features/PromptbookRuntime/connection/useStateUpdate";
import { setupDB } from "@/features/PromptbookRuntime/db/setupDB";
import { createRunbook } from "@/features/PromptbookRuntime/runbook/createRunbook";
import { createHostAPI } from "@/features/PromptbookRuntime/sandbox/createHostAPI";
import { useUpdatePromptbookState } from "@/features/Promptbooks/hooks/usePromptbook";
import { promptbook_editor_upload_file_event } from "@/services/posthog/events";
import { useAnalytics } from "@/services/posthog/hooks";
import { AbsoluteCenter, Spinner, useToast } from "@chakra-ui/react";
import { logger } from "@pufflig/logger";
import { PromptbookState, RunbookRequestMessage, v2 } from "@pufflig/prompt-studio-types";
import { EmptyState } from "@pufflig/prompt-studio-ui";
import { describeResources, getQueryContext, safeStringify, shortUID } from "@pufflig/prompt-tools";
import { createSandbox, SandboxRunOptions } from "@pufflig/promptbook-code-sandbox";
import {
  PromptbookEditor as PromptbookEditorComponent,
  PromptbookEditorRef,
  PromptbookProvider,
} from "@pufflig/promptbook-editor";
import { usePromptbookRuntime } from "@pufflig/promptbook-runtime";
import * as Sentry from "@sentry/react";
import { NuqsAdapter } from "nuqs/adapters/react";
import { useCallback, useEffect, useRef } from "react";
import { renamePromptbook } from "../actions/renamePromptbook";
import { runSQl } from "../actions/runSQL";
import { useConnectorSchema } from "../hooks/useConnectorSchema";
import { useConnectorTablePreview } from "../hooks/useConnectorTablePreview";
import { usePromptbookFeatureFlags } from "../hooks/usePromptbookFeatureFlags";
import { generateSuggestions } from "../actions/generateSuggestions";

interface PromptbookEditorProps {
  promptbookId: string;
  teamId?: string;
  promptbook?: v2.BasePromptbookState;
}

export const PromptbookEditor = (props: PromptbookEditorProps) => {
  const { promptbookId, promptbook, teamId } = props;
  const featureFlags = usePromptbookFeatureFlags();
  const { runtime, isLoading, error } = usePromptbookRuntime();
  const { connection, isConnected } = useConnection();
  const updatePromptbookState = useUpdatePromptbookState();
  const toast = useToast();
  const analytics = useAnalytics();

  const ref = useRef<PromptbookEditorRef>(null);

  useStateUpdate(connection, ref);

  const { data: connectorResources, isLoading: isLoadingConnectors } = useConnectorResources(teamId);

  const isLoadingPromptbook = isLoading || isLoadingConnectors;

  const onError = useCallback(
    async (error: any) => {
      const hasNoCredits =
        error?.status === 402 ||
        error?.response?.status === 402 ||
        (typeof error === "string" && error.includes("402"));

      toast({
        title: hasNoCredits ? "Insufficient Credits" : "Error",
        description: hasNoCredits
          ? "You no longer have credits left to run queries"
          : typeof error === "string"
            ? error
            : error?.message,
        status: "error",
        position: "top",
        isClosable: true,
      });

      Sentry.captureException(error);
    },
    [toast],
  );

  const onRemoveStep = async ({ results }: { results: string[] }) => {
    results.forEach((resultId) => {
      runtime?.storage.removeResult(resultId);
    });
  };

  const onPromptbookChange = useCallback(
    async (state: v2.BasePromptbookState) => {
      updatePromptbookState({ promptbookId, state });
    },
    [promptbookId],
  );

  const onUploadFile = async (files?: File[]): Promise<v2.Resource[]> => {
    if (!runtime) throw new Error("Runtime not initialized");
    const resources = await runtime.storage.uploadFiles(files);

    analytics.capture(promptbook_editor_upload_file_event, { count: resources.length });

    describeResources(resources, runtime).then((processedResources) => {
      const _resources = resources.map((res) => {
        return {
          ...res,
          ...(processedResources[res.resourceId]?.success || {}),
          isReady: true,
          error: processedResources[res.resourceId]?.error,
        };
      });

      ref.current?.completeResourceUpload(_resources);
    });

    return resources;
  };

  const onLoadFile = async (id: string): Promise<File> => {
    if (!runtime) throw new Error("Runtime not initialized");
    logger.debug(`(PromptbookEditor) Loading file with id: ${id}`);
    const file = await runtime.storage.loadRawFile(id);
    return file;
  };

  const onRunCode = async (code: string) => {
    if (!runtime) throw new Error("Runtime not initialized");
    if (!connection) throw new Error("Connection not established");

    const runOptions: SandboxRunOptions = {
      mode: "cache",
    };

    const host = createHostAPI({ connection, db: runtime.db });
    const sandbox = await createSandbox(host, runOptions);
    return sandbox.run(code);
  };

  const onRunSQL = async (sql: string, connectorId?: string): Promise<v2.ToolResult<object[]>> => {
    if (!runtime) throw new Error("Runtime not initialized");

    try {
      if (connectorId) {
        const tableResult = await runSQl(sql, connectorId);
        return { success: tableResult.table };
      }

      const res = await runtime.db.query(sql);
      if (res.error || !res.success) {
        return { success: null, error: res.error };
      }

      return { success: res.success };
    } catch (e: any) {
      return { success: null, error: e.message };
    }
  };

  const onEditResult = async (result: v2.Result) => {
    if (!runtime) throw new Error("Runtime not initialized");
    await runtime.storage.saveResult(result.metadata.resultId, result);
  };

  const onRunStep = useCallback(
    async (step: v2.QueryStep, promptbookState: v2.PromptbookState) => {
      if (!runtime) throw new Error("Runtime not initialized");
      if (!isConnected || !connection) throw new Error("Connection not established");

      if (!step.isRenamed) {
        renamePromptbookStep(step.query).then((newName) => {
          ref.current?.renameStep(step.stepId, newName);
        });
      }

      const threadId = `${step.stepId}_${shortUID()}`;

      const { runbook, resources } = await createRunbook(promptbookState, { stepId: step.stepId });

      // eventually nest db setup in a schema with threadId
      // (allowing different threads to interact with different parts of the db)
      await setupDB(runtime, resources);

      const payload: RunbookRequestMessage = {
        type: "runbook_request",
        threadId,
        runbook,
      };

      connection.send(safeStringify(payload));

      renamePromptbook(promptbookId, safeStringify(promptbookState));

      return threadId;
    },
    [runtime, connection, isConnected],
  );

  const onLoadConnectorTable = useConnectorTablePreview(runtime);
  const onLoadConnectorSchema = useConnectorSchema();

  const onRequestSuggestions = async (stepId: string, promptbook: PromptbookState) => {
    const { steps, externalResources, resources } = promptbook;
    const references = steps[stepId]?.references || [];

    const relevantResources = Object.values({ ...resources, ...externalResources }).filter((r) =>
      references.some((ref) => ref.sourceId === r.resourceId),
    );

    const query = steps[stepId]?.query || "";
    const context = await getQueryContext(relevantResources);
    const contextStr = safeStringify(context);
    const { suggestions } = await generateSuggestions(query, contextStr);

    return suggestions;
  };

  useEffect(() => {
    if (error) {
      onError(error);
    }
  }, [error, onError]);

  if (error) {
    return <div>Error Loading Runtime: {error.message}</div>;
  }

  if (promptbook?.version !== "1.3") {
    return (
      <AbsoluteCenter>
        <EmptyState
          showBackground
          title="Unsupported Promptbook Version"
          description="This promptbook is not compatible with the current version of Prompt Studio."
        />
      </AbsoluteCenter>
    );
  }

  if (isLoadingPromptbook) {
    return (
      <AbsoluteCenter>
        <Spinner />
      </AbsoluteCenter>
    );
  }

  if (!promptbook) {
    return (
      <AbsoluteCenter>
        <EmptyState showBackground title="Missing Promptbook" description="Couldn't load the Promptbook" />
      </AbsoluteCenter>
    );
  }

  const initialState: v2.PromptbookState = {
    ...promptbook,
    externalResources: {
      ...connectorResources,
    },
  };

  return (
    <NuqsAdapter>
      <PromptbookProvider
        key={promptbookId}
        namespace={promptbookId}
        initialState={initialState}
        onUploadFile={onUploadFile}
        onLoadFile={onLoadFile}
        onRunStep={onRunStep}
        onError={onError}
        onRemoveStep={onRemoveStep}
        onRunCode={onRunCode}
        onRunSQL={onRunSQL}
        onEditResult={onEditResult}
        onLoadConnectorSchema={onLoadConnectorSchema}
        onLoadConnectorTable={onLoadConnectorTable}
        onRequestSuggestions={onRequestSuggestions}
        featureFlags={featureFlags}
      >
        <PromptbookEditorComponent ref={ref} debounceTime={640} onPromptbookChange={onPromptbookChange} />
      </PromptbookProvider>
    </NuqsAdapter>
  );
};
